import { ClipboardCheck } from 'lucide-react'
import { Button } from '../../../../components'
import { formatDateTimeDisplay, formatTime12h } from '../constants/hrmsAttendanceDetails.constants'
import type { RegularizationRequest } from '../types/hrmsAttendanceDetails.types'

interface RegularizationRequestCardProps {
  request: RegularizationRequest
  onReview: (id: string) => void
}

export function RegularizationRequestCard({
  request,
  onReview,
}: RegularizationRequestCardProps) {
  const isPending = request.status === 'Pending Approval'

  return (
    <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200 shadow-xs space-y-3 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardCheck className="h-4 w-4 text-[#146971] shrink-0" />
          <span className="text-sm font-bold text-slate-900 truncate">Regularization Request</span>
        </div>

        {request.status === 'Pending Approval' && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 border border-amber-200 whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
            Pending Approval
          </span>
        )}
        {request.status === 'Approved' && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 border border-emerald-200 whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
            Approved
          </span>
        )}
        {request.status === 'Rejected' && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700 border border-red-200 whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
            Rejected
          </span>
        )}
      </div>

      <div className="bg-white rounded-lg p-3 border border-slate-200/80 space-y-2.5 text-xs">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-slate-500">Current Check In</div>
            <div className="font-bold text-slate-900 mt-0.5">
              {formatTime12h(request.currentCheckIn)}
            </div>
          </div>
          <div>
            <div className="text-slate-500">Requested Check In</div>
            <div className="font-bold text-[#146971] mt-0.5">
              {formatTime12h(request.requestedCheckIn)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-slate-500">Current Check Out</div>
            <div className="font-bold text-slate-900 mt-0.5">
              {formatTime12h(request.currentCheckOut)}
            </div>
          </div>
          <div>
            <div className="text-slate-500">Requested Check Out</div>
            <div className="font-bold text-[#146971] mt-0.5">
              {formatTime12h(request.requestedCheckOut)}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-2 space-y-1">
          <div className="text-slate-500 font-medium">Reason</div>
          <p className="text-slate-700 italic break-words">{request.reason}</p>
        </div>

        <div className="border-t border-slate-100 pt-2 flex items-center justify-between text-xs">
          <span className="text-slate-500">Requested On:</span>
          <span className="font-semibold text-slate-800">
            {formatDateTimeDisplay(request.requestedAt)}
          </span>
        </div>
      </div>

      {isPending && (
        <Button
          variant="primary"
          size="md"
          icon={<ClipboardCheck className="h-4 w-4" />}
          onClick={() => onReview(request.id)}
          className="w-full font-semibold justify-center"
        >
          Review Request
        </Button>
      )}
    </div>
  )
}
